import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

export default function AdminLayout() {
  const { user, signOut, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-stroke/70 bg-bgDeep/60 backdrop-blur">
        <div className="h-16 border-b border-stroke/70 flex items-center px-5 gap-2">
          <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-primary to-primaryDeep grid place-items-center">
            <span className="font-extrabold text-white">W</span>
          </div>
          <div>
            <div className="font-extrabold leading-tight">WordRace</div>
            <div className="text-[11px] font-semibold text-textMuted tracking-widest uppercase">
              Admin
            </div>
          </div>
        </div>

        <nav className="p-3 space-y-1">
          <SideLink to="/admin" end icon="📊" label="Dashboard" />
          <SideLink to="/admin/reports" icon="🚩" label="Şikayetler" />
          <SideLink to="/admin/flagged" icon="⚠️" label="İşaretliler" />
          <SideLink to="/admin/users" icon="👥" label="Kullanıcılar" />
          <SideLink to="/admin/rooms" icon="🎮" label="Odalar" />
          <SideLink to="/admin/quiz" icon="🧠" label="Soru Bankası" />
          <SideLink to="/admin/rejected" icon="🧩" label="Reddedilenler" />
          <SideLink to="/admin/bot" icon="🤖" label="Bot Rakip" />
          <SideLink to="/admin/roles" icon="🔑" label="Yetkiler" />
        </nav>

        <div className="mt-auto p-3 border-t border-stroke/70">
          <div className="rounded-xl bg-surface/70 p-3 mb-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="text-textMuted">
                {isSuperAdmin ? "Proje sahibi" : "Yönetici"}
              </span>
              {isSuperAdmin && (
                <span className="pill bg-gold/20 text-gold text-[9px] px-1.5 py-0.5">
                  SÜPER
                </span>
              )}
            </div>
            <div
              className="font-semibold truncate"
              title={user?.email ?? ""}
            >
              {user?.email ?? "—"}
            </div>
          </div>
          <button
            className="btn-ghost w-full justify-center text-sm"
            onClick={handleSignOut}
          >
            Çıkış Yap
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="md:hidden h-14 border-b border-stroke/70 flex items-center justify-between px-4 bg-bg/90 backdrop-blur sticky top-0 z-20">
          <div className="font-extrabold">WordRace · Admin</div>
          <button className="btn-ghost text-xs" onClick={handleSignOut}>
            Çıkış
          </button>
        </header>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-20 border-t border-stroke/70 bg-bg/95 backdrop-blur flex">
          <BottomLink to="/admin" end icon="📊" label="Özet" />
          <BottomLink to="/admin/reports" icon="🚩" label="Rapor" />
          <BottomLink to="/admin/flagged" icon="⚠️" label="Flag" />
          <BottomLink to="/admin/users" icon="👥" label="Oyuncu" />
          <BottomLink to="/admin/quiz" icon="🧠" label="Banka" />
          <BottomLink to="/admin/rejected" icon="🧩" label="Eksik" />
          <BottomLink to="/admin/bot" icon="🤖" label="Bot" />
          <BottomLink to="/admin/roles" icon="🔑" label="Yetki" />
        </nav>

        <main className="flex-1 px-4 md:px-8 py-6 pb-24 md:pb-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SideLink({
  to,
  icon,
  label,
  end,
}: {
  to: string;
  icon: string;
  label: string;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 px-3 py-2.5 rounded-xl font-semibold text-sm transition",
          isActive
            ? "bg-primary/20 text-textPrimary"
            : "text-textSecondary hover:bg-surface/60 hover:text-textPrimary",
        ].join(" ")
      }
    >
      <span className="text-lg leading-none">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

function BottomLink({
  to,
  icon,
  label,
  end,
}: {
  to: string;
  icon: string;
  label: string;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "flex-1 flex flex-col items-center py-2 gap-0.5 text-[10px] font-bold tracking-wide",
          isActive ? "text-primarySoft" : "text-textMuted",
        ].join(" ")
      }
    >
      <span className="text-lg">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}
